import { useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

export function WorkflowVideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showTapToUnmute, setShowTapToUnmute] = useState(false);

  const startPlayback = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    videoRef.current.volume = volume;
    const playPromise = videoRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        if (videoRef.current) { videoRef.current.muted = true; videoRef.current.play(); setShowTapToUnmute(true); setIsMuted(true); }
      });
    }
    setShowOverlay(false);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); } else { startPlayback(); }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    if (!newMuted) videoRef.current.volume = volume;
    setIsMuted(newMuted);
    setShowTapToUnmute(false);
  };

  const handleVolumeChange = (val: number[]) => {
    if (!videoRef.current) return;
    const v = val[0];
    setVolume(v);
    videoRef.current.volume = v;
    if (v === 0) { videoRef.current.muted = true; setIsMuted(true); }
    else if (isMuted) { videoRef.current.muted = false; setIsMuted(false); }
    setShowTapToUnmute(false);
  };

  return (
    <section id="workflow-video" className="py-20 md:py-28 bg-foreground">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
              Workflow Experience
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Precision in Motion: The 2-Minute Workflow.
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              See how LINK eliminates the &lsquo;Integrity Gap&rsquo; from intent to payment in just 120 seconds.
            </p>
          </div>

          <div className="mx-auto w-full max-w-4xl">
            <div className="relative aspect-video rounded-xl border border-gray-700 overflow-hidden bg-black group">
              <video
                ref={videoRef} playsInline preload="metadata" className="w-full h-full object-contain"
                src="/videos/2-minute-workflow.mp4"
                onPlay={() => { setIsPlaying(true); setShowOverlay(false); }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => { setIsPlaying(false); setShowOverlay(true); }}
              />
              {showTapToUnmute && !showOverlay && (
                <button onClick={() => { if (videoRef.current) { videoRef.current.muted = false; videoRef.current.volume = volume; setIsMuted(false); setShowTapToUnmute(false); } }}
                  className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-2 rounded-md bg-card border border-border text-primary text-xs font-medium min-h-[44px]">
                  <VolumeX className="h-4 w-4" /> Tap to Unmute
                </button>
              )}
              {showOverlay && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer" onClick={startPlayback}>
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-cta/20 border border-cta/40 flex items-center justify-center hover:scale-110 transition-transform">
                    <Play className="h-9 w-9 md:h-10 md:w-10 text-white ml-1" strokeWidth={1.5} />
                  </div>
                </div>
              )}
              <div className="absolute bottom-0 inset-x-0 p-3 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                </button>
                <button onClick={toggleMute} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                  {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <Slider value={[isMuted ? 0 : volume]} max={1} step={0.05} onValueChange={handleVolumeChange}
                  className="w-24 [&_[data-radix-slider-track]]:bg-white/20 [&_[data-radix-slider-range]]:bg-white [&_[data-radix-slider-thumb]]:border-white [&_[data-radix-slider-thumb]]:bg-white" />
              </div>
            </div>
          </div>

          <div className="mt-12 md:mt-16 max-w-3xl mx-auto text-center space-y-4">
            <h3 className="text-xl md:text-2xl font-bold text-white">
              Clinical Reality. Automated.
            </h3>
            <p className="text-gray-400 leading-relaxed">
              This isn&rsquo;t just a demo. It&rsquo;s the clinical standard for revenue integrity, built by
              surgeons who have had their finger on the pulse of surgical billing for decades. See
              the &lsquo;Triple-Match&rsquo; in action:{' '}
              <span className="text-white font-medium">Authorized</span>,{' '}
              <span className="text-white font-medium">Documented</span>, and{' '}
              <span className="text-white font-medium">Confirmed</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
